import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { PickForStyled } from 'utils/types';

type BadgeDivProps = PickForStyled<
    BadgeProps,
    'textColor' | 'backgroundColor' | 'position' | 'fontSize'
>;

const StyledBadge = styled.div<BadgeDivProps>`
    display: inline-block;
    background: ${({ $backgroundColor }) => $backgroundColor ?? 'unset'};
    color: ${({ $textColor }) => $textColor};
    border-radius: 1.2rem;
    min-width: 1.4rem;
    padding: 1px 2px;
    text-align: center;
    font-size: ${({ $fontSize }) => $fontSize};

    position: absolute;
    ${(props) => calculatePos(props)};
`;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

function calculatePos({ $position: { corner, inset } }: BadgeDivProps) {
    const xField = corner === 'topleft' || corner === 'bottomleft' ? 'left' : 'right';
    const yField = corner === 'topleft' || corner === 'topright' ? 'top' : 'bottom';

    return css`
        ${xField}: ${inset};
        ${yField}: ${inset};
    `;
}

type Corner = 'topleft' | 'topright' | 'bottomleft' | 'bottomright';

type BadgeContent = { icon: IconDefinition } | { number: number | undefined };
export type BadgeProps = Readonly<
    BadgeContent & {
        textColor: string;
        backgroundColor?: string;
        className?: string;
        position: { corner: Corner; inset: string };
        fontSize?: string;
        onClick?: () => void;
    }
>;

const Badge = forwardRef((props: BadgeProps, ref: Ref<HTMLDivElement>) => (
    <StyledBadge
        $textColor={props.textColor}
        $backgroundColor={props.backgroundColor}
        $position={props.position}
        $fontSize={props.fontSize}
        className={props.className}
        onClick={props.onClick}
        ref={ref}
    >
        {'icon' in props ? (
            <FontAwesomeIcon icon={props.icon} color={props.textColor} />
        ) : (
            props.number?.toString()
        )}
    </StyledBadge>
));

export type BadgeWrapperProps = BadgeProps & {
    badgeHidden?: boolean;
    children: JSX.Element;
};

export const BadgeWrapper = forwardRef(
    (props: BadgeWrapperProps, ref: Ref<HTMLDivElement>) => (
        <Wrapper>
            {props.children}
            {!props.badgeHidden && (
                <Badge
                    {...props}
                    ref={ref}
                    fontSize={props.fontSize ? props.fontSize : '1.2rem'}
                />
            )}
        </Wrapper>
    )
);
